import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import type { DailyRevenuePoint } from '@/api/reports-extra';
import { formatDate, money, moneyCompact } from '@/lib/utils';

export function RevenueChart({ data }: { data: DailyRevenuePoint[] }) {
  if (data.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">
        No sales in this period.
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={280}>
      <AreaChart data={data} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
        <defs>
          <linearGradient id="revenueFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#FF6B4D" stopOpacity={0.35} />
            <stop offset="100%" stopColor="#FF6B4D" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
        <XAxis
          dataKey="date"
          tickFormatter={(value: string) => formatDate(value).slice(0, 5)}
          stroke="hsl(var(--muted-foreground))"
          fontSize={11}
          tickLine={false}
          axisLine={false}
        />
        <YAxis
          tickFormatter={(value: number) => moneyCompact(value)}
          stroke="hsl(var(--muted-foreground))"
          fontSize={11}
          tickLine={false}
          axisLine={false}
          width={56}
        />
        <Tooltip
          content={({ active, payload, label }) => {
            if (!active || !payload?.[0]) return null;
            const point = payload[0].payload as DailyRevenuePoint;
            return (
              <div className="rounded-md border border-border bg-popover px-3 py-2 text-xs shadow-lg">
                <p className="font-medium">{formatDate(label as string)}</p>
                <p className="tabular mt-0.5">{money(point.revenue)}</p>
                <p className="text-muted-foreground">{point.count} bill(s)</p>
              </div>
            );
          }}
        />
        <Area type="monotone" dataKey="revenue" stroke="#FF6B4D" strokeWidth={2} fill="url(#revenueFill)" />
      </AreaChart>
    </ResponsiveContainer>
  );
}